import React, { useRef, useState, useEffect, useCallback } from "react";
import { Row, Col, Container, Button, Nav, Form, Card } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  getPatientById,
  getAppointmentById,
  getServiceById,
  fetchNotesByAppointmentId,
  deleteSessionById,
  getSessionById,
} from "../../api";
import { FollowUpSession } from "./FollowUpSession";
import { UpdateSession } from "./EditSession";
import { PrevSessions } from "../Sessions/PrevSessions";
import { PatientReports } from "../PatientReports/patientReports";
import { LinkNotes } from "./LinkNotes";
import PatientFileManager from "./PatientFiles";
import Diagnosis from "./Diagnosis";
import { useRecording } from "../SyncTo/RecordingProvider";
import SessionNoteRail, {
  deriveKind,
  expandNoteRows,
  noteTitleOf,
} from "./SessionNoteRail";
import SyncToButton, { hasSyncToAccess } from "../SyncTo/SyncToButton";
import RecordModal from "../SyncTo/RecordModal";
import ClinicalHistoryFullPanel from "../ClinicalHistory/ClinicalHistoryFullPanel";
import { userCanSeeClinicalHistory } from "../ClinicalHistory/access";

// Sections that belong to the appointment-notes panel. "write" is one of them:
// leaving it out made Add → Write render an empty page.
const APPT_SUBTABS = ["apptTemplate", "apptNotepad", "apptMedia", "write"];

export default function Session({
  extraMobileMenu = [],
  onMobileNavigate,
  patient: patientProp,
  appointment: appointmentProp,
  // When the page owns the nav rail (patientsappointment.jsx), it drives the
  // active section from outside and hides Session's own button row / drawer.
  controlledSection,
  onSectionChange,
  hideNav = false,
}) {
  const followUpRef = useRef();
  // Whichever editor is mounted (create or edit) — used to persist the open
  // note before switching away from it.
  const editorRef = useRef(null);
  const { id, appointmentId, sessionId } = useParams();
  const hasAppointment = appointmentId && appointmentId !== "1";
  const editingAdHoc = !!sessionId && !hasAppointment;

  // sub-sections: apptTemplate | apptNotepad | apptMedia
  const [activeSection, setActiveSection] = useState(
    appointmentId === "1" ? "historical" : "apptTemplate",
  );


  // Controlled-nav wiring (used by patientsappointment.jsx's rail).
  // - controlledSection: parent-driven section → sync into local state.
  // - onSectionChange: notify parent of any internal transition (e.g. a save
  //   that jumps to "historical") so the rail highlight stays in step.
  useEffect(() => {
    if (controlledSection && controlledSection !== activeSection) {
      setActiveSection(controlledSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSection]);

  useEffect(() => {
    onSectionChange?.(activeSection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // SyncTo modal: mode picker + consent + recording UI live in a single
  // combined RecordModal now (was two-modal flow with ModeModal). The
  // SyncToButton click resolves the session, then opens RecordModal directly
  // — RecordModal owns the mode picker / consent gate internally and PATCHes
  // /consent at the moment Start is clicked.
  const recording = useRecording();

  const [patient, setPatient] = useState(patientProp);
  const [appointment, setAppointment] = useState(appointmentProp);
  const [service, setService] = useState();
  // An appointment holds many notes now. `notes` is the rail's light list;
  // `existingSession` is the FULL document for whichever note is open, which
  // is what the editors below actually bind to.
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  // A note the user has added but not yet saved — it exists only in the rail
  // until the editor's first save gives it an id.
  const [draftNoteKind, setDraftNoteKind] = useState(null);
  // Incremented on every Add. The editor is keyed on it so a second note of the
  // SAME kind still gets a fresh component: keying on the kind alone made React
  // reuse the instance, carrying over the previous note's content and its
  // already-saved id, so the new note looked like it had overwritten the last.
  const [draftToken, setDraftToken] = useState(0);
  const [existingSessionId, setExistingSessionId] = useState(null);
  const [existingSession, setExistingSession] = useState(null);
  // The open note's kind and legacy-bundle flag, taken from its rail row.
  // NOT read off the session document: `sessionType` is absent on every note
  // saved before the one-kind-per-note split, and falling back to the page's
  // section state made each note open on whichever tab the previous one used.
  const [activeNoteKind, setActiveNoteKind] = useState(null);
  const [activeNoteLegacy, setActiveNoteLegacy] = useState(false);
  // The rail row that's open. For a legacy bundle several rows share one
  // document, so `existingSessionId` can't identify which is selected.
  const [activeRowId, setActiveRowId] = useState(null);
  // Bumped only when something outside the editor rewrites the open note (a
  // SyncTo apply). The editor's own saves must NOT remount it — that flash was
  // the "page refreshes on every save" behaviour.
  const [noteRemountToken, setNoteRemountToken] = useState(0);
  // Ad-hoc notes have no appointment to group under, so the notes made in this
  // sitting are tracked here rather than fetched. Deliberately not persisted —
  // on reload each ad-hoc note stands alone in Previous Notes.
  const [adHocSitting, setAdHocSitting] = useState([]);
  // Generated once per mount: every note saved in this ad-hoc sitting carries
  // it, so the sitting reopens as a group in Previous Notes instead of
  // scattering into unrelated single notes.
  const adHocGroupIdRef = useRef(null);
  if (!adHocGroupIdRef.current) {
    adHocGroupIdRef.current =
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `adhoc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isPortrait, setIsPortrait] = useState(true);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 1024);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isLandscape, setIsLandscape] = useState(window.matchMedia("(orientation: landscape)").matches);
  const isApptSubtab = APPT_SUBTABS.includes(activeSection);

  const formattedDateTime =
    appointment?.start && !isNaN(new Date(appointment.start))
      ? new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(appointment.start))
      : "Invalid Date";

  // Phase 30 — patientLabel passed to SyncToButton → RecordingProvider.openModal(meta)
  // so the IDB sessions row (D-06 / D-08) is populated with a human-readable
  // label for the orphan prompt. Derived from existing `appointment` state —
  // no new fetches.
  const patientFullName = [appointment?.patientFirstName, appointment?.patientLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const patientLabel = patientFullName
    ? `${patientFullName}${formattedDateTime && formattedDateTime !== "Invalid Date" ? ` — ${formattedDateTime}` : ""}`
    : (formattedDateTime && formattedDateTime !== "Invalid Date" ? formattedDateTime : "");

  const NotesHeader = ({ isAdHoc }) => {
    return (
      <div>
        <Row className="align-items-center">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              {isAdHoc ? (
                <p className="text-primary fw-bold mb-0"></p>
              ) : (
                <p className="text-primary fw-bold mb-0">
                  {formattedDateTime} &nbsp;-&nbsp;{" "}
                  {service?.servicesName || "Loading service name..."}
                </p>
              )}
            </div>
          </Col>
        </Row>

      </div>
    );
  };

  const renderAppointmentNotes = () => {
    const adHocRail = (
      <SessionNoteRail
        notes={adHocRailNotes}
        activeNoteId={draftNoteKind ? "__draft__" : existingSessionId}
        onSelect={handleSelectNote}
        onAdd={handleAddNote}
        addLeadingItems={syncToMenuItem}
        onDelete={handleDeleteNote}
        isMobile={isMobile}
      />
    );

    // --- EDITING AD-HOC (by sessionId) ---
    // Also enters this branch when the session was lazy-created on SyncTo click
    // (existingSession populated but URL lacks :sessionId, so editingAdHoc is
    // false). Without this, post-Apply refetch lands in `existingSession` but
    // FollowUpSession (create-mode) never reads it → UI looks unchanged until
    // page refresh.
    if (editingAdHoc || (!hasAppointment && existingSession && existingSession._id)) {
      return (
        <>
          {/* Editing an ad-hoc note reached from "Add Note" keeps the sitting's
              rail, so a second note can be added alongside it. Opening one
              directly by URL (:sessionId) has no sitting to show. */}
          {editingAdHoc ? <NotesHeader isAdHoc /> : noteToolbar}
          {existingSessionId ? (
            <UpdateSession
              key={`edit-adhoc-${existingSessionId}-${noteRemountToken}`}
              ref={editorRef}
              sessionType={activeNoteKind || existingSession.sessionType}
              noteRail={editingAdHoc ? undefined : adHocRail}
              hideInlineSyncTo={!editingAdHoc}
              sessionId={existingSession._id}
              appointmentId={existingSession.sessionsAppointmentId || null} // null for ad-hoc
              practiceId={existingSession.sessionsPracticeId}
              patientId={id}
              sections={existingSession.sections}
              notepadContentR={existingSession.notepadContent || ""}
              notepadContentType={existingSession.notepadContentType || ""}
              // Without this the write canvas opened blank on a saved
              // handwritten note — and the next stroke exported over the page
              // that was already there.
              handwritingImageR={existingSession.handwritingImage || null}
              mediaFilesR={existingSession.mediaFiles || []}
              appointmentStatus={existingSession.appointmentStatus || null}
              serviceName={existingSession.sessionsServiceName || "Ad Hoc"}
              appointmentStart={
                existingSession.sessionsAppointmentStart ||
                existingSession.createdAt
              }
              templateId={existingSession.sessionsTemplateId}
              resourceId={existingSession.sessionsResourceId || null}
              activeSection={activeSection}
              onSaved={() => handleAdHocSaved(existingSessionId)}
              capturedValuesR={existingSession.capturedValues || {}}
              sessionsTemplateVersionR={existingSession.sessionsTemplateVersion}
              updatedAtR={existingSession.updatedAt || null}
              syncToProps={syncToProps}
            />
          ) : (
            <p>Loading session…</p>
          )}
        </>
      );
    }

    // --- AD-HOC CREATE (no appointment, no sessionId) ---
    // Same shape as the appointment view: a rail of the notes made in this
    // sitting plus Add, so an ad-hoc visit can hold a template AND a note.
    // The grouping is in-memory only — ad-hoc notes have no shared key, so on
    // reload each one stands alone in Previous Notes.
    if (!hasAppointment) {
      if (!draftNoteKind && !existingSessionId) {
        return (
          <>
            {noteToolbar}
            <div className="d-flex" style={{ marginTop: "4px" }}>
              <div
                className="border-end p-2"
                style={
                isMobile
                  // Bounded rather than free: the rows carry their titles on
                  // mobile now, and an unconstrained rail would grow to fit
                  // them and squeeze the editor beside it. Titles truncate.
                  ? { width: "40%", maxWidth: 190, flexShrink: 0 }
                  : { width: "210px", flexShrink: 0 }
              }
              >
                {adHocRail}
              </div>
              <div className="p-4 flex-grow-1 text-center text-muted">
                <i className="fa-light fa-note-medical d-block mb-2" style={{ fontSize: "1.75rem" }} />
                <p className="mb-0">
                  Use <strong>Add</strong> to start a note, or <strong>SyncTo</strong> to record.
                </p>
              </div>
            </div>
          </>
        );
      }

      return (
        <>
          {noteToolbar}
          <FollowUpSession
            key={`adhoc-${id}-${draftNoteKind || "new"}-${draftToken}`}
            ref={(node) => { followUpRef.current = node; editorRef.current = node; }}
            adHoc
            appointmentId={null}
            patientId={id}
            serviceName={""}
            resourceId={null}
            appointmentStart={null}
            appointmentStatus={null}
            practiceId={undefined}
            activeSection={activeSection}
            sessionType={draftNoteKind || undefined}
            adHocGroupId={adHocGroupIdRef.current}
            onUpdate={(newId, opts) => handleAdHocSaved(newId, opts)}
            syncToProps={syncToProps}
            noteRail={adHocRail}
            hideInlineSyncTo
          />
        </>
      );
    }

    // --- APPOINTMENT BRANCH (unchanged) ---
    if (!appointment) return <p>Loading appointment details...</p>;

    // Phase 34 follow-up bug #1 — include updatedAt in the key so a post-apply
    // refetch of existingSession (which appends the [SyncTo] block to
    // notepadContent) forces UpdateSession to remount. Without this, the
    // notepad-content effect inside EditSession.jsx only runs once per
    // sessionId, so the fresh prop is ignored until a full page refresh.
    const upKey = `up-${existingSessionId || "none"}-${noteRemountToken}`;
    // Keyed on the draft kind too, so picking "Add → Note" after "Add → Template"
    // remounts the editor into the new kind instead of reusing the old one.
    const createKey = `fu-${appointmentId}-${draftNoteKind || "new"}-${draftToken}`;

    // Render UpdateSession only when we have the FULL session doc — when only
    // existingSessionId is populated (e.g. SyncTo lazy-created a session but
    // hasn't refetched the doc yet) we fall through to FollowUpSession so a
    // null `existingSession._id` access doesn't crash render.
    if (existingSessionId && existingSession && existingSession._id) {
      return (
        <>
          {noteToolbar}
          <UpdateSession
            key={upKey}
            ref={editorRef}
            sessionId={existingSession._id}
            appointmentId={existingSession.sessionsAppointmentId}
            practiceId={existingSession.sessionsPracticeId}
            patientId={id}
            sections={existingSession.sections}
            notepadContentR={existingSession.notepadContent || ""}
            notepadContentType={existingSession.notepadContentType || ""}
            handwritingImageR={existingSession.handwritingImage || null}
            mediaFilesR={existingSession.mediaFiles || []}
            appointmentStatus={appointment.status}
            serviceName={service?.servicesName || ""}
            appointmentStart={appointment.start}
            templateId={existingSession.sessionsTemplateId}
            resourceId={appointment.resourceId}
            activeSection={activeSection}
            // Stay on the appointment after saving. This used to jump to
            // "historical" (and EditSession navigated to /…/1, dropping the
            // appointmentId from the URL) — now the note list refreshes in place
            // so adding another note is one click away.
            onSaved={() => loadNotes(existingSessionId, { reopen: false })}
            capturedValuesR={existingSession.capturedValues || {}}
            sessionsTemplateVersionR={existingSession.sessionsTemplateVersion}
            updatedAtR={existingSession.updatedAt || null}
            syncToProps={syncToProps}
            sessionType={activeNoteKind || existingSession.sessionType}
            legacyBundle={activeNoteLegacy}
            alreadyInvoiced={!!appointment?.isInvoiced}
            serviceId={appointment?.serviceId}
            noteRail={<SessionNoteRail {...noteRailProps} />}
            hideInlineSyncTo
          />
        </>
      );
    }

    // Nothing open and nothing chosen — don't drop the user straight into a
    // template picker. Opening an appointment shouldn't presume the note is
    // going to be a template; the picker appears once Add → Template is chosen.
    if (!draftNoteKind) {
      return (
        <>
          {noteToolbar}
          <div className="d-flex" style={{ marginTop: "4px" }}>
            <div
              className="border-end p-2"
              style={
                isMobile
                  // Bounded rather than free: the rows carry their titles on
                  // mobile now, and an unconstrained rail would grow to fit
                  // them and squeeze the editor beside it. Titles truncate.
                  ? { width: "40%", maxWidth: 190, flexShrink: 0 }
                  : { width: "210px", flexShrink: 0 }
              }
            >
              <SessionNoteRail {...noteRailProps} />
            </div>
            <div className="p-4 flex-grow-1 text-center text-muted">
              <i className="fa-light fa-note-medical d-block mb-2" style={{ fontSize: "1.75rem" }} />
              <p className="mb-0">
                No notes on this appointment yet — use <strong>Add</strong> to start
                one, or <strong>SyncTo</strong> to record.
              </p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {noteToolbar}
        <FollowUpSession
          key={createKey}
          ref={(node) => { followUpRef.current = node; editorRef.current = node; }}
          appointmentId={appointmentId}
          patientId={id}
          serviceName={service?.servicesName || ""}
          resourceId={appointment.resourceId}
          appointmentStart={appointment.start}
          appointmentStatus={appointment.status}
          practiceId={appointment.practiceId}
          activeSection={activeSection}
          // Stay on the appointment: refresh the rail and open the note that was
          // just created, rather than navigating away to Previous Notes.
          onUpdate={(newId, opts) => loadNotes(newId, { reopen: !opts?.stayPut })}
          syncToProps={syncToProps}
          sessionType={draftNoteKind || undefined}
          noteRail={<SessionNoteRail {...noteRailProps} />}
          hideInlineSyncTo
        />
      </>
    );
  };

  const doAndCollapse = (fn, payload) => {
    try {
      fn?.();
    } finally {
      setIsSidebarCollapsed(true);
      onMobileNavigate?.(payload);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
      setIsNarrow(window.innerWidth <= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)");
    const onChange = (e) => setIsPortrait(e.matches ?? mql.matches);
    onChange(mql);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  // Sync prop → state when parent (Patientsappointment) supplies these and
  // they arrive after first paint. Patientsappointment fetches the same docs
  // via its own effects; passing them in lets us skip duplicate API calls.
  // Convention: prop === undefined ⇒ "parent isn't supplying, fetch yourself";
  // prop === null ⇒ "parent is loading, wait"; prop populated ⇒ use it.
  useEffect(() => {
    if (patientProp) setPatient(patientProp);
  }, [patientProp]);

  useEffect(() => {
    if (appointmentProp) {
      setAppointment(appointmentProp);
      setSelectedStatus(appointmentProp.status);
    }
  }, [appointmentProp]);

  useEffect(() => {
    async function loadPatientAndAppointment() {
      try {
        if (!appointmentId) return;

        const parentSuppliesPatient = patientProp !== undefined;
        const parentSuppliesAppointment = appointmentProp !== undefined;

        if (!parentSuppliesPatient) {
          const patientData = await getPatientById(id);
          setPatient(patientData);
        }

        if (!parentSuppliesAppointment) {
          const appointmentData = await getAppointmentById(appointmentId);
          setAppointment(appointmentData);
          setSelectedStatus(appointmentData.status);
        }

        // ensure correct default tab
        setActiveSection(appointmentId === "1" ? "historical" : "apptTemplate");
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadPatientAndAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appointmentId]);

  // Service lookup runs whenever appointment.serviceId becomes available —
  // whether appointment came from props or our own fetch.
  useEffect(() => {
    if (!appointment?.serviceId) return;
    let cancelled = false;
    getServiceById(appointment.serviceId)
      .then((s) => { if (!cancelled) setService(s); })
      .catch((err) => console.error("Error fetching service:", err));
    return () => { cancelled = true; };
  }, [appointment?.serviceId]);

  /**
   * Load the appointment's notes and open one.
   *
   * `preferId` keeps the user on the note they were editing across a refresh
   * (after a save, or after a SyncTo apply); otherwise the newest note opens,
   * which is what someone returning to an appointment expects to see.
   */
  const loadNotes = useCallback(async (preferId, { reopen = true } = {}) => {
    if (!hasAppointment) return [];
    setNotesLoading(true);
    try {
      const list = await fetchNotesByAppointmentId(appointmentId);
      setNotes(list);
      // Refreshing the rail after a save must not re-fetch and re-bind the open
      // note: replacing the editor's props mid-edit is what made every save
      // look like a page reload.
      if (!reopen) return list;
      const wanted =
        (preferId && list.find((n) => String(n._id) === String(preferId))) ||
        list[list.length - 1];
      if (wanted) {
        const [firstRow] = expandNoteRows(wanted);
        setDraftNoteKind(null);
        setActiveRowId(String(firstRow._id));
        setActiveNoteKind(firstRow.sessionType || null);
        setActiveNoteLegacy(!firstRow.isSplit && !!wanted.legacyBundle);
        if (SECTION_FOR_KIND[firstRow.sessionType]) {
          setActiveSection(SECTION_FOR_KIND[firstRow.sessionType]);
        }
        const full = await getSessionById(wanted._id);
        if (full && full._id) {
          setExistingSessionId(String(full._id));
          setExistingSession(full);
        }
      } else {
        // No notes on this appointment — the editor opens in create mode.
        setExistingSessionId(null);
        setExistingSession(null);
      }
      return list;
    } catch (error) {
      console.error("Error loading appointment notes:", error);
      return [];
    } finally {
      setNotesLoading(false);
    }
  }, [appointmentId, hasAppointment]);

  useEffect(() => {
    async function loadSession() {
      try {
        if (editingAdHoc && sessionId) {
          const s = await getSessionById(sessionId); // <- implement in ../../api if needed
          if (s && s._id) {
            setExistingSessionId(s._id);
            setExistingSession(s);
          }
          setIsLoading(false);
          return;
        }
        // Appointment view — load every note on the appointment. Skipped when
        // appointmentId is the "1" sentinel (ad-hoc / historical), which has no
        // appointment to list notes for.
        if (hasAppointment) {
          await loadNotes();
        } else {
          // "Add Note" / historical view — clear any stale existingSession
          // left over from a previous appointment-bound render so clicking
          // Add Note opens a fresh FollowUpSession (create mode) instead of
          // UpdateSession (edit mode) with the previous session's values.
          setNotes([]);
          setDraftNoteKind(null);
          setExistingSessionId(null);
          setExistingSession(null);
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadSession();
  }, [appointmentId, sessionId, editingAdHoc, hasAppointment, loadNotes]);

  const SECTION_FOR_KIND = {
    template: "apptTemplate",
    note: "apptNotepad",
    write: "write",
    media: "apptMedia",
  };

  // ── Note rail handlers ──────────────────────────────────────────────────
  // The rail lives inside the editors (that's where the column is), but every
  // handler belongs here because Session owns the notes list.

  /**
   * Save whatever note is currently open, if it has anything in it.
   *
   * Starting a second note used to silently discard the first unless the user
   * had pressed Submit. Both editors expose saveNow(); it no-ops on an
   * untouched note so an abandoned Add can't leave an empty one behind.
   */
  const saveOpenNote = useCallback(async () => {
    const save = editorRef.current?.saveNow;
    if (typeof save !== "function") return null;
    try {
      return await save();
    } catch (err) {
      console.error("Could not save the open note before switching:", err);
      return null;
    }
  }, []);

  const handleSelectNote = useCallback(async (note) => {
    if (!note?._id) return;
    await saveOpenNote();
    setDraftNoteKind(null);
    setActiveRowId(String(note._id));
    setActiveNoteKind(note.sessionType || null);
    // A split row shows exactly one kind, so it is never the compatibility
    // multi-tab view — the rail already offers the bundle's other kinds.
    setActiveNoteLegacy(!note.isSplit && !!note.legacyBundle);
    // Move the page's section to match, so a handwriting note opens on the
    // write canvas rather than inheriting the last note's tab.
    if (SECTION_FOR_KIND[note.sessionType]) {
      setActiveSection(SECTION_FOR_KIND[note.sessionType]);
    }
    // Split rows point at a shared document via realId.
    const full = await getSessionById(note.realId || note._id);
    if (full && full._id) {
      setExistingSessionId(String(full._id));
      setExistingSession(full);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveOpenNote]);

  /**
   * Start a new note of `kind`. The note being left is saved first; nothing is
   * written for the NEW note until its editor saves, so an abandoned Add can't
   * leave an empty note on the appointment.
   */
  const handleAddNote = useCallback(async (kind) => {
    const savedId = await saveOpenNote();
    if (savedId) await loadNotes(savedId, { reopen: false });
    setDraftToken((t) => t + 1);
    setDraftNoteKind(kind);
    setActiveRowId("__draft__");
    setActiveNoteKind(kind);
    setActiveNoteLegacy(false);
    setExistingSessionId(null);
    setExistingSession(null);
    // "write" was falling through to the template tab here, so Add → Write
    // opened the wrong editor.
    setActiveSection(SECTION_FOR_KIND[kind] || "apptTemplate");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveOpenNote, loadNotes]);

  const handleDeleteNote = useCallback(async (note) => {
    if (!note?._id || note.isSplit) return;

    // A draft has never been saved, so its rail id is the literal "__draft__"
    // sentinel rather than a real session id. Handing that to the API is what
    // produced "Could not delete this note" while the row stayed on the rail —
    // the id was rejected as malformed, so nothing was ever deleted. Discarding
    // a draft is purely local state.
    if (note.isDraft || note._id === "__draft__") {
      setDraftNoteKind(null);
      setDraftToken((t) => t + 1);
      if (hasAppointment) {
        // Re-binds the rail to the most recent saved note, or clears it.
        await loadNotes();
      } else {
        // Ad-hoc has no server-side list to reload from — fall back to the
        // last note in this sitting, or to nothing when the draft was the only
        // one. Leaving activeRowId pointing at "__draft__" would highlight a
        // row that no longer exists.
        const last = adHocSitting[adHocSitting.length - 1] || null;
        setActiveRowId(last ? String(last._id) : null);
        setActiveNoteKind(last ? last.sessionType || null : null);
      }
      return;
    }

    try {
      await deleteSessionById(note.realId || note._id);
    } catch (_err) {
      alert("Could not delete this note — please try again.");
      return;
    }
    await loadNotes();
  }, [loadNotes, hasAppointment, adHocSitting]);

  const adHocRailNotes = draftNoteKind
    ? [
        ...adHocSitting,
        {
          _id: "__draft__",
          sessionType: draftNoteKind,
          sessionsNoteTitle: "New note",
          isDraft: true,
        },
      ]
    : adHocSitting;

  /** An ad-hoc note was saved — add it to this sitting and keep editing it. */
  const handleAdHocSaved = useCallback(async (newId, opts) => {
    if (!newId) return;
    let full = null;
    try {
      full = await getSessionById(newId);
    } catch (_err) {
      full = null;
    }
    setAdHocSitting((prev) => {
      const row = {
        _id: String(newId),
        sessionType: deriveKind(full || {}),
        sessionsNoteTitle: noteTitleOf(full || {}),
        legacyBundle: false,
      };
      return prev.some((n) => String(n._id) === String(newId))
        ? prev.map((n) => (String(n._id) === String(newId) ? row : n))
        : [...prev, row];
    });
    // `stayPut` means the save happened because the user is on their way
    // somewhere else (Add, another note, a recording). Record it in the sitting
    // but leave the editor alone — re-opening it here would land after the
    // caller has set up the next note and silently replace it.
    if (opts?.stayPut) return;
    setDraftNoteKind(null);
    if (full && full._id) {
      setExistingSessionId(String(full._id));
      setExistingSession(full);
      setActiveNoteKind(deriveKind(full));
    }
  }, []);

  // A legacy note holding several kinds expands into one row per kind.
  const expandedNotes = notes.flatMap((n) => expandNoteRows(n));


  useEffect(() => {
    if (appointment) setSelectedStatus(appointment.status);
  }, [appointment]);

  useEffect(() => {
    if (!isMobile && activeSection === "apptMedia") {
      setActiveSection("apptTemplate");
    }
  }, [isMobile, activeSection]);

  // Phase 32 — SyncTo Apply post-200 handler.
  // Switches to the Note Pad tab and refetches the session so the appended
  // [SyncTo <ISO>] block renders via the existing notepadContent path.
  // D-08 / D-08a — refetch via existing endpoint; no new socket event.
  const onSyncToApplied = useCallback(async ({ mode, sessionId: sessionIdApplied } = {}) => {
    if (mode === 'note') {
      setActiveSection('apptNotepad');
    }
    if (mode === 'template') {
      // Phase 33 — Template Apply success switches to the appointment template
      // tab so the doctor immediately sees the written capturedValues rendered
      // via EditSession → NoteRenderer (readonly mode). Refetch path below is
      // shared with the Note branch so the rendered values are fresh.
      setActiveSection('apptTemplate');
    }
    try {
      if (hasAppointment) {
        // Apply may have CREATED the note (a capture that was never bound to
        // one), so reload the whole list rather than refetching a single doc,
        // and open whichever note the server wrote into.
        await loadNotes(sessionIdApplied || existingSessionId);
        setNoteRemountToken((t) => t + 1);
      } else if (existingSessionId || sessionIdApplied) {
        // Ad-hoc / historical session: appointmentId is the "1" sentinel,
        // so refetch by the known session id instead (bug from Phase 32
        // exposed by Phase 34's CORS fix — the appointment route 404s on "1").
        const refetched = await getSessionById(sessionIdApplied || existingSessionId);
        if (refetched && refetched._id) {
          setExistingSessionId(String(refetched._id));
          setExistingSession(refetched);
        }
      }
    } catch (_err) {
      // R11 / Q10 mitigation — refetch failure is non-fatal (write already
      // happened server-side). User can manually refresh to see changes.
    }
  }, [hasAppointment, existingSessionId, loadNotes]);

  // Listen for the global 'syncto:applied' event so this Session refreshes
  // itself even when meta.onApplied is stale (modal opened via socket event
  // or after a notification-click navigation that remounted this Session
  // instance with existingSessionId still null).
  useEffect(() => {
    const handler = async (e) => {
      const detail = (e && e.detail) || {};
      const matchesSession = existingSessionId && detail.sessionId &&
        String(detail.sessionId) === String(existingSessionId);
      const matchesAppointment = hasAppointment && detail.sessionId &&
        existingSession && String(existingSession._id) === String(detail.sessionId);
      const matchesPatient = detail.patientId && id &&
        String(detail.patientId) === String(id);

      // Adopt the event's sessionId when:
      //   - we already track it (matchesSession / matchesAppointment), OR
      //   - we have no session yet but the event is for this patient
      //     (ad-hoc create flow that remounted via the notification click —
      //     existingSessionId is null but we still need to refresh the UI).
      if (!matchesSession && !matchesAppointment && !matchesPatient) return;

      if ((!matchesSession && !matchesAppointment) && matchesPatient && detail.sessionId) {
        try {
          const fresh = await getSessionById(detail.sessionId);
          if (fresh && fresh._id) {
            setExistingSessionId(String(fresh._id));
            setExistingSession(fresh);
            if (detail.mode === 'note') setActiveSection('apptNotepad');
            else if (detail.mode === 'template') setActiveSection('apptTemplate');
            return;
          }
        } catch (_err) { /* fall through to onSyncToApplied */ }
      }
      onSyncToApplied({ mode: detail.mode });
    };
    window.addEventListener('syncto:applied', handler);
    return () => window.removeEventListener('syncto:applied', handler);
  }, [existingSessionId, existingSession, hasAppointment, id, onSyncToApplied]);

  // Practice for a capture made with no appointment and no open note. Mirrors
  // the ad-hoc fallback the old lazy-session path used (FollowUpSession's Save
  // resolves practice the same way).
  const resolveJwtPracticeId = () => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return null;
      const decoded = jwtDecode(token);
      if (Array.isArray(decoded?.practice) && decoded.practice.length > 0) {
        return String(decoded.practice[0]);
      }
    } catch (_err) {
      // Malformed token — caller surfaces the "practice not resolved" path.
    }
    return null;
  };

  // SyncTo click interceptor — opens the combined RecordModal directly. The
  // modal owns the mode picker, template selection, and consent gate.
  //
  // No session is created here any more. A capture is filed against the patient
  // and /apply creates the note it lands in, so clicking SyncTo no longer leaves
  // an empty session behind when the user records nothing or discards. We pass
  // `sessionId` only when a note is already open, so a recording made while
  // editing still lands in that note. We return { sessionId: null } so
  // SyncToButton bails out of its own openModal call (we just made it).
  const onSyncToBeforeOpen = useCallback(async () => {
    // Starting a recording used to discard an unsaved note. Persist it first
    // and record against it, so the transcript lands in the note the user is
    // looking at rather than a fresh one.
    const savedId = await saveOpenNote();
    if (savedId) await loadNotes(savedId, { reopen: false });
    const practiceId =
      appointment?.practiceId ||
      existingSession?.sessionsPracticeId ||
      resolveJwtPracticeId();
    if (!practiceId) {
      alert('Could not resolve practice for this recording — please refresh and try again.');
      return { sessionId: null, error: new Error('Practice not resolved') };
    }
    recording?.openModal({
      appointmentId: hasAppointment ? appointmentId : null,
      patientId: id,
      practiceId,
      patientLabel,
      sessionId: savedId || existingSessionId || null,
      // existingCapturedValues seeds the Template-mode review preview when
      // the user is editing a session that already has captured fields.
      existingCapturedValues: (existingSession && existingSession.capturedValues) || {},
      onApplied: onSyncToApplied,
    });
    return { sessionId: null };
  }, [
    recording,
    appointmentId,
    hasAppointment,
    id,
    appointment,
    patientLabel,
    existingSessionId,
    existingSession,
    onSyncToApplied,
    saveOpenNote,
    loadNotes,
  ]);

  // Phase 34 follow-up — SyncToButton was relocated from Session.jsx's top-level
  // sidebar into the FollowUpSession / UpdateSession nav columns. This bundle
  // threads the Session-owned callbacks (lazy session creation, ModeModal open,
  // post-apply refetch) down so the button still behaves identically.
  const syncToProps = {
    appointmentId,
    patientLabel,
    sessionId: existingSessionId,
    onApplied: onSyncToApplied,
    onBeforeOpen: onSyncToBeforeOpen,
  };

  /**
   * SyncTo as the first entry in Add's menu.
   *
   * Recording is one of the ways to start a note, not a separate thing done
   * beside them — so it belongs in the same menu as Template / Note / Write /
   * Media rather than in a button next to it. SyncToButton renders the entry
   * itself so the feature-flag gate and the browser-support check stay in one
   * place; when the flag is off it returns null and Add's menu is just the
   * kinds, with no divider left stranded above them.
   */
  // null rather than an element that renders null — see AddNoteDropdown: the
  // divider above the note kinds keys off whether there IS a leading item, and
  // a <SyncToButton /> element is truthy either way.
  const syncToMenuItem = hasSyncToAccess()
    ? <SyncToButton {...syncToProps} asDropdownItem />
    : null;

  const noteRailProps = {
    notes: draftNoteKind
      ? [
          ...expandedNotes,
          {
            _id: "__draft__",
            sessionType: draftNoteKind,
            sessionsNoteTitle: "New note",
            isDraft: true,
          },
        ]
      : expandedNotes,
    activeNoteId: draftNoteKind ? "__draft__" : activeRowId,
    onSelect: handleSelectNote,
    onAdd: handleAddNote,
    addLeadingItems: syncToMenuItem,
    onDelete: handleDeleteNote,
    busy: notesLoading,
    isMobile,
  };

  /**
   * The line above the editor box — now only the appointment's date and
   * service.
   *
   * It used to carry SyncTo and Add as well. Both moved into the note rail's
   * Add menu, beside the notes they create: Add because a control for making
   * notes belongs with the list of them, SyncTo because recording is one of
   * the ways to start a note rather than a separate action.
   *
   * Review Needed / Dismissed are deliberately absent too. Those lists are
   * patient-wide, so offering them while composing one appointment's note (or
   * an ad-hoc one) invited acting on a capture belonging to a different visit
   * from the one on screen. They live on Previous Notes, the patient-wide view,
   * which is also where an applied or dismissed capture lands.
   */
  const noteToolbar = hasAppointment ? (
    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
      <span className="text-primary fw-bold text-truncate" style={{ minWidth: 0 }}>
        {formattedDateTime}
        {service?.servicesName ? ` — ${service.servicesName}` : ""}
      </span>
    </div>
  ) : null;

  if (isLoading) return <p>Loading session data...</p>;

  // ------- Sidebar items (add Link Notes after Previous Notes) -------
  const appointmentKeys = isMobile
    ? APPT_SUBTABS
    : ["apptTemplate", "apptNotepad"];

  const parentIsActive = appointmentKeys.includes(activeSection);

  const shortLabels = isLandscape || isNarrow;
  const canSeeClinicalHistory = userCanSeeClinicalHistory();

  const sidebarItems = [
    {
      key: "appointment",
      label: hasAppointment
        ? (shortLabels ? "Appointment" : "Appointment Notes")
        : "Add Note",
      icon: "fa-light fa-note-medical",
    },
    {
      key: "historical",
      label: shortLabels ? "Previous" : "Previous Notes",
      icon: "fa-sharp fa-light fa-notes",
    },
    ...(canSeeClinicalHistory ? [{
      key: "history",
      label: shortLabels ? "History" : "AI History",
      icon: "fa-light fa-clock-rotate-left",
    }] : []),
    {
      key: "diagnosis",
      label: "Diagnoses",
      icon: "fa-sharp fa-light fa-user-doctor-message",
    },
    {
      key: "linkNotes",
      label: shortLabels ? "Link" : "Link Notes",
      icon: "fa-light fa-paperclip",
    },
    {
      key: "files",
      label: shortLabels ? "Files" : "Patient Files",
      icon: "fa-light fa-cabinet-filing",
    },
    {
      key: "reports",
      label: shortLabels ? "Reports" : "Generate Reports",
      icon: "fa-light fa-file-arrow-down",
    },
  ];

  const titleMap = {
    apptTemplate: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    apptNotepad: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    apptMedia: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    historical: "PREVIOUS SESSIONS",
    diagnosis: "DIAGNOSES",
    linkNotes: "LINK NOTES",
    files: "PATIENT FILES",
    reports: "GENERATE REPORTS",
  };

  const renderContent = () => {
    if (APPT_SUBTABS.includes(activeSection)) {
      return renderAppointmentNotes();
    }
    if (activeSection === "historical") return <PrevSessions patientId={id} />;

    if (activeSection === "linkNotes") {
      return (
        <LinkNotes
          patientId={id}
          patientFirstName={appointment?.patientFirstName}
          patientLastName={appointment?.patientLastName}
          onSuccess={() => setActiveSection("historical")}
        />
      );
    }

    if (activeSection === "files") return <PatientFileManager patientId={id} />;
    if (activeSection === "reports") return <PatientReports patientId={id} />;
    if (activeSection === "diagnosis") {
      return <Diagnosis patientId={id} />;
    }
    if (activeSection === "history" && canSeeClinicalHistory) {
      return (
        <ClinicalHistoryFullPanel
          active
          patientId={id}
          practiceId={
            appointment?.practiceId ||
            existingSession?.sessionsPracticeId ||
            (Array.isArray(patient?.patientPractice) ? patient.patientPractice[0] : undefined)
          }
          patientName={patientFullName || patient?.patientFullNames || ""}
          showHeader={false}
        />
      );
    }

    return null;
  };

  return (
    <div className="ms-1">
      {/* ── MOBILE: collapsible sidebar drawer ── */}
      {isMobile && !hideNav && (
        <>
          {/* Arrow toggle */}
          <div
            className="position-fixed d-flex align-items-center justify-content-center bg-primary text-white shadow-lg"
            style={{
              left: isSidebarCollapsed ? "10px" : "250px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "20px",
              height: "40px",
              borderRadius: "0 8px 8px 0",
              cursor: "pointer",
              transition: "left 0.3s ease",
              zIndex: 1050,
            }}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            <i
              className={`bi ${isSidebarCollapsed ? "bi-chevron-right" : "bi-chevron-left"}`}
              style={{ fontSize: "18px" }}
            />
          </div>

          {/* Overlay */}
          {!isSidebarCollapsed && (
            <div
              className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
              style={{ top: 0, left: 0, zIndex: 1040 }}
              onClick={() => setIsSidebarCollapsed(true)}
            />
          )}

          {/* Drawer */}
          <div
            className="position-fixed bg-white shadow-lg d-flex flex-column"
            style={{
              left: isSidebarCollapsed ? "-250px" : "0",
              top: "0",
              width: "250px",
              height: "100vh",
              transition: "left 0.3s ease",
              zIndex: 1045,
            }}
          >
            <div className="p-3 border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold text-primary">Menu</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsSidebarCollapsed(true)}
                >
                  ×
                </Button>
              </div>
            </div>

            <div className="flex-grow-1 overflow-auto">
              {extraMobileMenu.length > 0 && (
                <>
                  <div className="px-3 pt-3 text-muted small">Patient</div>
                  <div className="p-3">
                    {extraMobileMenu.map((item) => (
                      <div key={item.key} className="mb-2">
                        <Button
                          variant="outline-primary"
                          className="w-100 text-start"
                          onClick={() => doAndCollapse(item.onClick, "patient")}
                        >
                          {item.label}
                        </Button>
                      </div>
                    ))}
                  </div>
                  <hr className="my-0" />
                </>
              )}

              <div className="p-3 pt-3">
                <div className="text-muted small mb-2">Session</div>
                {sidebarItems.map((item) => {
                  const isActive =
                    item.key === "appointment"
                      ? parentIsActive
                      : activeSection === item.key;
                  return (
                    <div key={item.key} className="mb-2">
                      <Button
                        variant={isActive ? "primary" : "outline-primary"}
                        className="w-100 text-start d-flex align-items-center gap-2"
                        onClick={() => {
                          setActiveSection(
                            item.key === "appointment" ? "apptTemplate" : item.key,
                          );
                          doAndCollapse(null, "session");
                        }}
                      >
                        <i className={item.icon} />
                        <span>{item.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── DESKTOP: centered top row of buttons with title pinned left ── */}
      {!isMobile && !hideNav && (
        <div className="d-flex align-items-start mt-2 pb-2">
          {/* Left: title — takes equal share so center is truly centred */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
           {/*} <h5 className="text-primary mt-3 mb-0">{titleMap[activeSection] || ""}</h5> */}
          </div>
          {/* Centre: buttons */}
          <div className="d-flex justify-content-center flex-wrap gap-1 mt-3" style={{ flex: "0 1 auto" }}>
            {sidebarItems.map((item, idx) => (
              <React.Fragment key={item.key}>
                {idx === 6 && isNarrow && <div style={{ width: "100%" }} />}
                <Button
                  variant={
                    (item.key === "appointment" ? parentIsActive : activeSection === item.key)
                      ? "primary"
                      : "outline-primary"
                  }
                  className="rounded-0"
                  style={{ minWidth: "120px" }}
                  onClick={() =>
                    setActiveSection(
                      item.key === "appointment" ? "apptTemplate" : item.key,
                    )
                  }
                >
                  <i className={`${item.icon} me-2`} />
                  {item.label}
                </Button>
              </React.Fragment>
            ))}
          </div>
          {/* Right: spacer matching left so buttons stay centred */}
          <div style={{ flex: "1 1 0" }} />
        </div>
      )}

      {/* Content area */}
      <div className="pt-1">
        {renderContent()}
      </div>

      {/* Combined RecordModal — owns mode picker, consent, and recording UI.
          Rendered exactly once per Session mount so it unmounts on in-app
          nav (SC4) while the RecordingProvider state above the router keeps
          the recorder alive. Driven entirely by useRecording() — no props
          required here. */}
      <RecordModal />

    </div>
  );
}
